<template>
  <main class="el-core">
    <onboarding v-if="onboarding" />
    <div class="el-core__header">
      <app-core-header />
    </div>
    <section class="el-core__wrapper">
      <div class="el-main">
        <div class="el-main__content">
          <div class="el-bounds _narrow eu-mt_5_md">
            <emd-alert class="eu-mt_0 eu-no-mb eu-mt_5" v-if="isTestAccount" variant="info" noClose :autoclose="false">
              <template slot="content">
                <p class="emd-text _color_info" v-html="$t('Webapp.Testflag.title')"></p>
              </template>
            </emd-alert>
            <read-only-alert/>
            <div class="el-section eu-mt_5" v-if="showBreadcrumb && accountEnabled">
              <emd-breadcrumb />
            </div>
            <div class="eu-mt_3" v-if="showTabBar && accountEnabled">
              <emd-tabbar :justify="'space-between'" bordered />
            </div>
            <div class="eu-mt_3" v-if="showTabList && accountEnabled">
              <h2 class="emd-text _color_secondary _size_page" v-if="isMobile">{{ mobileTitle.label.value }}</h2>
              <emd-tab-list
                v-else
                bordered
                :items="tabListItems"
                :activeItem="paymentLinkStatus"
                @changed="changeActiveItem"
              />
            </div>
            <transition :name="transitionName" mode="out-in">
              <router-view v-if="accountEnabled" />
              <continue-config v-else-if="canConfig" />
              <no-access v-else />
            </transition>
            <div class="el-footer">
              <transition name="fade" mode="out-in">
                <emd-footer />
              </transition>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import ContinueConfig from '@/components/project/ContinueConfig'
import NoAccess from '@/components/project/NoAccess'
import EmdFooter from '@/components/emerald/organisms/EmdFooter'
import EmdTabbar from '@/components/emerald/molecules/EmdTabbar'
import EmdTabList from '@/components/emerald/molecules/EmdTabList'
import { EmdAlert } from 'emerald-vue/src/components'
import EmdBreadcrumb from '@/components/emerald/molecules/EmdBreadcrumb'
import Onboarding from '@/components/project/Onboarding'
import AppCoreHeader from '@/components/project/ui/header/AppCoreHeader'
import ReadOnlyAlert from '@/components/project/ReadOnlyAlert'

export default {
  name: 'Webapp',
  components: {
    Onboarding,
    ContinueConfig,
    NoAccess,
    EmdFooter,
    EmdTabbar,
    EmdTabList,
    EmdAlert,
    EmdBreadcrumb,
    AppCoreHeader,
    ReadOnlyAlert
  },
  data () {
    return {
      transitionName: 'slide-left',
      tabListItems: [
        {
          label: {
            key: 'products-services',
            value: this.$t('tabListItems.products')
          }
        },
        {
          label: {
            key: 'link-settings',
            value: this.$t('tabListItems.link')
          }
        },
        {
          label: {
            key: 'payment-methods',
            value: this.$t('tabListItems.payment')
          }
        }
      ]
    }
  },
  computed: {
    mobileTitle () {
      return this.tabListItems.find(item => item.label.key === this.paymentLinkStatus)
    },
    isMobile () {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    },
    paymentLinkStatus () {
      return this.$store.getters.paymentLinkStatus
    },
    accountEnabled () {
      return (
        this.$store.getters.accountEnabled &&
        (this.permissionIsEnabled('shop.account:get'))
      )
    },
    componentTabbar () {
      return this.$store.getters.routesWizard.filter(item => this.$route.name === item.component)[0].tabbar.length
    },
    isTestAccount () {
      return this.$store.getters.profile.merchant.account.account_type.key === 'test'
    },
    isCheckoutV2Enabled () {
      return this.$store.getters.accountSummary.checkout_v2_enabled
    },
    showTabBar () {
      return (this.accountEnabled && this.componentTabbar) || (this.initialRouteName && !this.hasSubscriptionEnabled && !this.isCheckoutV2Enabled)
    },
    showTabList () {
      return (this.$route.name === 'PaymentLink' || this.$route.name === 'Link' || this.$route.name === 'PaymentMethods')
    },
    initialRouteName () {
      return this.$route.name === 'OrderType'
    },
    showBreadcrumb () {
      return !this.initialRouteName
    },
    hasSubscriptionEnabled () {
      return this.$store.getters.hasSubscriptionEnabled
    },
    canConfig () {
      return this.$store.getters.missingConfig.length
    },
    onboarding () {
      return !this.$store.getters.isEmployee && this.accountEnabled && (this.$store.getters.onboarding.show_greeting || !this.$store.getters.order_created)
    }
  },
  methods: {
    permissionIsEnabled (action) {
      return this.$store.getters.permissionIsEnabled(action)
    },
    changeActiveItem (item) {
      this.$store.dispatch('CHANGE_STATUS', item.label.key)
      if (item.label.key === 'products-services') {
        this.$router.push({ name: 'PaymentLink' })
      } else if (item.label.key === 'link-settings') {
        this.$router.push({ name: 'Link' })
      } else if (item.label.key === 'payment-methods') {
        this.$router.push({ name: 'PaymentMethods' })
      }
    }
  },
  watch: {
    '$route' (to, from) {
      const toDepth = to.path.split('/').length
      const fromDepth = from.path.split('/').length
      this.transitionName = toDepth < fromDepth ? 'slide-right' : 'slide-left'
    }
  }
}
</script>

<style>
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition-duration: 500ms;
  transition-property: opacity, transform;
  transition-timing-function: cubic-bezier(0.55, 0, 0.1, 1);
  overflow: hidden;
}

.slide-left-enter,
.slide-right-leave-active {
  opacity: 0;
  transform: translateX(calc(2em - 6px));
}

.slide-left-leave-active,
.slide-right-enter {
  opacity: 0;
  transform: translateX(calc(-2em - 6px));
}

.label-optional {
  font-weight: normal !important
}
</style>

<i18n>
{
  "pt-BR": {
    "tabListItems": {
      "products": "1. PRODUTOS OU SERVIÇOS",
      "link": "2. LINK",
      "payment": "3. FORMAS DE PAGAMENTO"
    }
  },
  "en-US": {
    "tabListItems": {
      "products": "1. PRODUCTS OR SERVICES",
      "link": "2. LINK",
      "payment": "3. PAYMENT METHODS"
    }
  }
}
</i18n>
